import React, { useState, useRef, useEffect } from 'react';
import { Accordion, AccordionItemsWithIds } from '@jbhi-fi/lanyard-ui/components/Accordion';
import JBButton from '@jbhi-fi/lanyard-ui/components/Button';
import { intersectionObserver } from '../../../../utils/intersection-observer';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { useProductAccordion } from '../AccordionContext';
import ProductOverviewContent from './ProductOverviewContent';
import SyndicatedContent from './SyndicatedContent';
import { SyndicatedContentData } from '../syndicatedContent/syndicatedContent';
import { SyndicatedContentProductData, SyndicatedContentProvider } from '../syndicatedContent/types';
import DetailsContent from './DetailsContent';
import TracklistContent from './TracklistContent';
import TermsOfServiceContent from './TermsOfServiceContent';
import constants from './constants';
import { styles } from './AccordionContent.css';
import ReviewsContent from './ReviewsContent';
import { isSoftware } from '../utils/utils';
import NegativeIcon from '@jbhi-fi/lanyard-ui/components/Icons/Negative';
import PositiveIcon from '@jbhi-fi/lanyard-ui/components/Icons/Positive';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { DimensionsContent, splitDimensions } from './DimensionsContent';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { MarketplaceSellerInfoContent } from './MarketplaceSellerInfoContent';
import { ProductMessages } from '@jbhi-fi/components/src/ProductDetail/ProductMessages';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export enum AccordionIds {
  ProductOverview = 'product-overview',
  FromTheSupplier = 'from-the-supplier',
  TermsOfService = 'terms-of-service',
  Details = 'details',
  Dimensions = 'dimensions',
  Reviews = 'reviews',
  TrackList = 'track-list',
  MarketplaceSellerInfo = 'marketplace-seller-info',
}

const AccordionContent = (): JSX.Element => {
  const isSmallViewport = useBreakpoint('md', 'down');
  const { shopify, metafields, productMessages, marketplaceSellers } = useShopifyProduct();
  const fullProductOverviewSkus = window.Jbhifi.pdpAccordion?.showFullProductOverviewSkus?.split(',');
  const shouldShowFullProductOverview = fullProductOverviewSkus?.includes(shopify?.variants[0]?.sku);
  const accordion = useProductAccordion();
  const accordionItems: Array<AccordionItemsWithIds> = [];

  const [readMoreEnabled, setReadMoreEnabled] = useState(true);
  const [syndicatedContent, setSyndicatedContent] = useState<Array<SyndicatedContentProvider | null>>([]);
  const [syndicatedContentItem, setSyndicatedContentItem] = useState<AccordionItemsWithIds | undefined>(undefined);
  const [overviewIsOpen, setOverviewIsOpen] = useState(false);

  const accordionContainer = useRef<HTMLDivElement | null>(null);
  const overviewContainer = useRef<HTMLDivElement | null>(null);
  const { Product, Department, ProductGroup, Display, Availability } = metafields.online_product.value;
  const productOverview = shopify?.description || Display?.OnlineDescription;
  const productSku = shopify?.variants[0]?.sku;
  const dimensionsProductGroupIds = window.Jbhifi.dimensionsProductGroupIds?.split(',');
  const productGroupId = ProductGroup?.Id?.toString() ?? '';
  const shouldShowDimensions = dimensionsProductGroupIds?.includes(productGroupId);
  const hideSyndicatedContentBrands = window.Jbhifi?.pdpAccordion?.hideSyndicatedContentBrands
    ?.toLowerCase()
    .split(',');
  const shouldHideSyndicatedContent = hideSyndicatedContentBrands?.includes(Product.Brand?.toLowerCase());
  const syndicatedContentTopSkus = window.Jbhifi?.pdpAccordion?.syndicatedContentTopSkus?.split(',');
  const syndicatedContentIndex = syndicatedContentTopSkus?.includes(productSku) ? 0 : 1;

  const data: SyndicatedContentProductData = {
    productSku: productSku,
    productModel: Product?.Model,
    productGroup: ProductGroup?.Id?.toString() ?? '',
    productPLU: shopify?.variants[0]?.barcode,
    productEAN13: Product?.Barcode,
    productBrand: encodeURIComponent(Product?.Brand),
  };
  const details = Display?.SpecificationDetails;
  const trackList = Display?.Tracklist;
  const termsOfService = Display?.TermsAndConditions;
  const numberOfReviews = metafields?.online_product?.value?.Display?.NumberOfProductReviews ?? 0;
  const imsReviewsEnabled = Display.ShowReviews;
  const themeReviewsEnabled = window.Jbhifi?.pdpReviewsEnabled;
  const shouldHideReviews =
    !imsReviewsEnabled ||
    (Availability?.Fulfilment === 'Digital' && Availability?.ProductLifecycle === 'PreOrder') ||
    (!themeReviewsEnabled && imsReviewsEnabled && numberOfReviews < 1);

  const openCloseIcons = {
    closeIcon: <NegativeIcon />,
    openIcon: <PositiveIcon />,
  };
  const ALLOWED_DETAILS = ['Music Genre Primary', 'Label'];
  const FORBIDDEN_DETAILS = ['Primary Format - Games', 'Primary Format - Movies/TV'];
  const marketplaceSeller = marketplaceSellers?.[0];
  const isMarketplaceProduct =
    ShopifyProductTransformer.isMarketplaceProduct({ product: shopify }) && Boolean(marketplaceSeller);

  const shouldShowDetails = (detail: any) => {
    if (Department?.Id === 100) return ALLOWED_DETAILS.includes(detail.Name);
    if (Department?.Id === 92 || Department?.Id === 110) return !FORBIDDEN_DETAILS.includes(detail.Name);
    return true;
  };

  const formatDetail = (detail: any) => {
    if (detail.Name === 'Music Genre Primary' || detail.Name === 'Film Genre' || detail.Name === 'Game Genre')
      return { Name: 'Genre', Values: detail.Values };
    return detail;
  };

  const shouldRenderDetailsAccordion = () => {
    return !isSoftware(Department.Id) && Availability.Fulfilment !== 'Digital';
  };

  const filteredDetails = details?.length ? details.filter(shouldShowDetails).map(formatDetail) : [];
  const overviewContainerHeight = overviewContainer.current?.offsetHeight ?? 0;
  const isContentUnderReadMoreLimit =
    (isSmallViewport && overviewContainerHeight < constants.readMoreMinMobile) ||
    (!isSmallViewport && overviewContainerHeight < constants.readMoreMinDesktop);

  useEffect(() => {
    setOverviewIsOpen(!!shouldShowFullProductOverview);
  }, [shouldShowFullProductOverview]);

  const handleOverviewClick = () => {
    if (overviewIsOpen) {
      const siteHeader = document.querySelector('#shopify-section-header') as HTMLElement | null;

      if (siteHeader && accordionContainer.current) {
        const scrollTopPosition =
          window.scrollY + accordionContainer.current.getBoundingClientRect().top - siteHeader.offsetHeight;

        setTimeout(() => window.scrollTo(0, scrollTopPosition), 0);
      }
    }

    if (!overviewIsOpen && isContentUnderReadMoreLimit) {
      setReadMoreEnabled(false);
    }

    setOverviewIsOpen(!overviewIsOpen);
  };

  useEffect(() => {
    if (overviewIsOpen && isContentUnderReadMoreLimit) {
      setReadMoreEnabled(false);
      return;
    }

    if (!readMoreEnabled && !isContentUnderReadMoreLimit) {
      setReadMoreEnabled(true);
    }
  }, [isContentUnderReadMoreLimit, overviewIsOpen, readMoreEnabled]);

  const syndicatedContentData = new SyndicatedContentData(data);

  useEffect(() => {
    if (shouldHideSyndicatedContent || !accordionContainer.current) return;

    const updateProviders = async () => {
      const providers = await syndicatedContentData.getProviders();
      setSyndicatedContent(providers);
    };

    updateProviders();
  }, [accordionContainer]);

  // Product Overview
  if (productOverview) {
    accordionItems.push({
      urlHashId: AccordionIds.ProductOverview,
      head: (
        <CalloutFont as="h2" className={styles.accordionTitle}>
          Description
        </CalloutFont>
      ),
      content: (
        <>
          <div className={[styles.collapsible, !overviewIsOpen && styles.collapsed].join(' ')}>
            <div ref={overviewContainer}>
              <ProductMessages allowedTypes={['General', 'Important']} messages={productMessages} />
              <ProductOverviewContent content={productOverview} />
              {filteredDetails.length && !shouldRenderDetailsAccordion() ? (
                <div className={styles.detailsWrapper}>
                  <DetailsContent content={filteredDetails} />
                </div>
              ) : null}
            </div>
          </div>
          {readMoreEnabled ? (
            <div className={styles.readMore}>
              <JBButton
                icon={overviewIsOpen ? <NegativeIcon /> : <PositiveIcon />}
                iconPosition="left"
                label={overviewIsOpen ? 'Read less' : 'Read more'}
                onClick={handleOverviewClick}
                type="linkWithIcon"
              />
            </div>
          ) : null}
        </>
      ),
      ...openCloseIcons,
    });
  }

  if (details?.length) {
    const { dimensions, otherDetails } = splitDimensions(details);

    // Details
    if (shouldRenderDetailsAccordion()) {
      accordionItems.push({
        urlHashId: AccordionIds.Details,
        head: <CalloutFont className={styles.accordionTitle}>Specs</CalloutFont>,
        content: <DetailsContent content={shouldShowDimensions ? otherDetails : details} />,
        ...openCloseIcons,
      });
    }

    // Dimensions
    if (shouldShowDimensions && dimensions?.length) {
      accordionItems.push({
        urlHashId: AccordionIds.Dimensions,
        head: <CalloutFont className={styles.accordionTitle}>Dimensions</CalloutFont>,
        content: <DimensionsContent dimensions={dimensions} productGroupId={productGroupId} />,
        ...openCloseIcons,
      });
    }
  }

  // Track List
  if (trackList?.length) {
    accordionItems.push({
      urlHashId: AccordionIds.TrackList,
      head: <CalloutFont className={styles.accordionTitle}>Track Listing</CalloutFont>,
      content: <TracklistContent content={trackList} />,
      ...openCloseIcons,
    });
  }

  // Terms of Service
  if (termsOfService) {
    accordionItems.push({
      urlHashId: AccordionIds.TermsOfService,
      head: <CalloutFont className={styles.accordionTitle}>Terms of Service</CalloutFont>,
      content: <TermsOfServiceContent content={termsOfService} />,
      ...openCloseIcons,
    });
  }

  // Reviews
  if (!shouldHideReviews) {
    accordionItems.push({
      urlHashId: AccordionIds.Reviews,
      head: <CalloutFont className={styles.accordionTitle}>Reviews</CalloutFont>,
      content: <ReviewsContent productSku={productSku} isActive={accordion.active.includes('reviews')} />,
      ...openCloseIcons,
    });
  }

  if (isMarketplaceProduct) {
    accordionItems.push({
      urlHashId: AccordionIds.MarketplaceSellerInfo,
      head: (
        <CalloutFont className={styles.accordionTitle}>
          Seller Info, Returns policy & JB Hi-Fi Marketplace Terms of Sale
        </CalloutFont>
      ),
      content: (
        <MarketplaceSellerInfoContent
          shippingInfoMdx={marketplaceSeller?.shipping_info}
          returnsRefundsPolicyMdx={marketplaceSeller?.returns_refunds_policy}
        />
      ),
      ...openCloseIcons,
    });
  }

  // Syndicated Content
  useEffect(() => {
    if (!syndicatedContent.length) return;

    const heading = `Info from ${data.productBrand ? decodeURIComponent(data.productBrand) : 'the supplier'}`;
    setSyndicatedContentItem({
      urlHashId: AccordionIds.FromTheSupplier,
      head: <CalloutFont className={styles.accordionTitle}>{heading}</CalloutFont>,
      content: <SyndicatedContent providers={syndicatedContent} />,
      ...openCloseIcons,
    });

    syndicatedContentData.loadContent(syndicatedContent);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [syndicatedContent.map(provider => provider?.type).join(',')]);

  if (syndicatedContentItem) {
    accordionItems.splice(syndicatedContentIndex, 0, syndicatedContentItem);
  }

  const gtmAccordionChange = (id: string) => {
    window.dataLayer.push({
      event: 'pdpAccordionClick',
      category: 'product',
      value: id,
    });
  };

  return (
    <div className={styles.root} ref={accordionContainer}>
      <Accordion
        useIds
        id="pdp-accordion"
        className={styles.accordion}
        accordionItemClassname={styles.accordionItem}
        accordionHeaderClassname={styles.accordionHead}
        accordionContentClassname={styles.accordionContent}
        items={accordionItems}
        multiOpen
        activeState={accordion.active}
        onChange={gtmAccordionChange}
        onActiveStateChange={accordion.setActive}
      />
    </div>
  );
};

export default AccordionContent;
